import { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { servicePages, servicePageUi, serviceOrder } from "@/i18n/servicePages";
import { useLenis } from "@/hooks/useLenis";
import Cursor from "@/components/Cursor";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

function ParallaxImage({ src, alt, offset = 40 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [`-${offset / 2}px`, `${offset}px`]);
  return (
    <div ref={ref} className="overflow-hidden aspect-[4/5]">
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ y }}
        className="w-full h-full object-cover"
      />
    </div>
  );
}

export default function ServiceDetail() {
  useLenis();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();

  const page = servicePages[lang]?.[slug];
  const ui = servicePageUi[lang];

  // Reset scroll on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const nextSlug = useMemo(() => {
    if (!slug) return null;
    const idx = serviceOrder.indexOf(slug);
    if (idx === -1) return null;
    return serviceOrder[(idx + 1) % serviceOrder.length];
  }, [slug]);

  const nextPage = nextSlug ? servicePages[lang]?.[nextSlug] : null;

  if (!page) {
    return (
      <div className="dark min-h-screen bg-[#050505] text-[color:var(--fg)] flex flex-col">
        <Navigation />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-6">
          <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight">
            {ui.notFoundTitle}
          </h1>
          <button
            onClick={() => navigate("/")}
            className="magnetic"
            data-testid="service-not-found-back"
          >
            {ui.notFoundBack} <span aria-hidden className="ml-3">→</span>
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className="dark relative bg-[#050505] text-[color:var(--fg)]"
      data-testid={`service-page-${slug}`}
    >
      <div className="noise" aria-hidden />
      <Cursor />
      <Navigation />

      <main className="pt-32 md:pt-40">
        {/* Breadcrumb */}
        <div className="px-6 md:px-12 mb-12">
          <div className="max-w-[1600px] mx-auto flex items-center gap-4 text-xs uppercase tracking-[0.28em] text-[color:var(--muted)]">
            <Link to="/" className="link-hover" data-testid="service-breadcrumb-home">
              ← {ui.breadcrumbBack}
            </Link>
            <span className="inline-block w-8 h-px bg-[color:var(--border)]" />
            <span className="text-[color:var(--fg)]">
              {ui.servicesLabel} / {page.name}
            </span>
          </div>
        </div>

        {/* Hero */}
        <section className="relative px-6 md:px-12 pb-24 md:pb-40">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-baseline gap-6 mb-10">
              <span className="text-outline-accent font-display font-extrabold text-6xl md:text-8xl leading-none">
                {page.number}
              </span>
              <span className="h-px flex-1 bg-[color:var(--border)]" />
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-[13vw] md:text-[10vw] lg:text-[8vw] font-extrabold leading-[0.88] tracking-[-0.04em]"
              data-testid="service-hero-name"
            >
              {page.name}
              <span className="text-[color:var(--accent)]">.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 font-display uppercase text-3xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] leading-[0.95] max-w-4xl whitespace-pre-line text-[color:var(--muted)]"
            >
              {page.tagline}
            </motion.p>
          </div>
        </section>

        {/* Intro two-column */}
        <section className="relative px-6 md:px-12 py-24 md:py-32 border-t hairline">
          <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-x-16">
            <div className="md:col-span-4">
              <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--muted)] flex items-center gap-4">
                <span className="inline-block w-8 h-px bg-[color:var(--accent)]" />
                {ui.introEyebrow}
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-8 space-y-8"
            >
              <p className="font-serif-italic text-2xl md:text-4xl leading-[1.25] text-[color:var(--fg)]">
                {page.intro}
              </p>
              {page.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-base md:text-lg text-[color:var(--muted)] leading-relaxed max-w-3xl"
                >
                  {p}
                </p>
              ))}
            </motion.div>
          </div>
        </section>

        {/* First image + capabilities */}
        <section className="relative px-6 md:px-12 py-24 md:py-32 border-t hairline">
          <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-y-16 md:gap-x-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-7"
            >
              <ParallaxImage src={page.gallery[0]} alt={page.name} offset={60} />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-5 md:pt-8"
            >
              <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--muted)] flex items-center gap-4 mb-10">
                <span className="inline-block w-8 h-px bg-[color:var(--accent)]" />
                {ui.capabilitiesEyebrow}
              </div>
              <ul className="border-t hairline" data-testid="service-capabilities">
                {page.capabilities.map((c, i) => (
                  <li
                    key={i}
                    className="border-b hairline py-5 flex items-baseline gap-6"
                  >
                    <span className="text-xs text-[color:var(--muted)] tracking-[0.22em]">
                      0{i + 1}
                    </span>
                    <span className="font-display uppercase text-lg md:text-xl tracking-[-0.01em]">
                      {c}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Approach */}
        <section className="relative px-6 md:px-12 py-24 md:py-32 border-t hairline">
          <div className="max-w-[1600px] mx-auto">
            <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--muted)] flex items-center gap-4 mb-16">
              <span className="inline-block w-8 h-px bg-[color:var(--accent)]" />
              {ui.approachEyebrow}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
              {page.approach.map((step, i) => (
                <motion.article
                  key={step.n}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="border-t hairline pt-6"
                >
                  <div className="text-outline-accent font-display font-extrabold text-5xl md:text-7xl leading-none mb-6">
                    {step.n}
                  </div>
                  <h3 className="font-display uppercase text-2xl md:text-3xl font-bold tracking-[-0.02em] mb-4">
                    {step.title}
                  </h3>
                  <p className="text-[color:var(--muted)] leading-relaxed">
                    {step.body}
                  </p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Quote */}
        <section className="relative px-6 md:px-12 py-32 md:py-48 border-t hairline">
          <div className="max-w-[1400px] mx-auto">
            <motion.blockquote
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif-italic text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-[-0.01em]"
            >
              <span className="text-[color:var(--accent)] mr-2">“</span>
              {page.quote}
              <span className="text-[color:var(--accent)] ml-2">”</span>
            </motion.blockquote>
          </div>
        </section>

        {/* Gallery */}
        <section className="relative px-6 md:px-12 py-24 md:py-32 border-t hairline">
          <div className="max-w-[1600px] mx-auto">
            <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--muted)] flex items-center gap-4 mb-16">
              <span className="inline-block w-8 h-px bg-[color:var(--accent)]" />
              {ui.galleryEyebrow}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-x-8">
              {page.gallery.slice(1).map((src, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className={
                    i === 0
                      ? "md:col-span-7 md:col-start-1"
                      : i === 1
                      ? "md:col-span-5 md:col-start-8"
                      : "md:col-span-8 md:col-start-3"
                  }
                >
                  <ParallaxImage src={src} alt={`${page.name} — ${i + 1}`} offset={40} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA + next service */}
        <section className="relative px-6 md:px-12 py-24 md:py-40 border-t hairline">
          <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-y-16 md:gap-x-12 items-end">
            <div className="md:col-span-7">
              <h2 className="font-display uppercase text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-[-0.03em] leading-[0.9]">
                {ui.ctaTitle}
                <span className="text-[color:var(--accent)]">.</span>
              </h2>
              <p className="mt-8 max-w-lg text-[color:var(--muted)] text-base md:text-lg leading-relaxed">
                {ui.ctaBody}
              </p>
              <Link
                to="/#contact"
                className="mt-10 inline-flex items-center gap-3 magnetic"
                data-testid="service-cta-contact"
              >
                {ui.ctaButton}
                <span aria-hidden>→</span>
              </Link>
            </div>

            {nextPage && (
              <Link
                to={`/services/${nextSlug}`}
                className="md:col-span-5 group border-l hairline pl-8 md:pl-12 block"
                data-testid={`service-next-${nextSlug}`}
              >
                <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--muted)] mb-4">
                  {ui.nextService} — {nextPage.number}
                </div>
                <div className="font-display uppercase text-4xl md:text-6xl font-extrabold tracking-[-0.03em] leading-none transition-colors duration-500 group-hover:text-[color:var(--accent)]">
                  {nextPage.name}
                </div>
                <div className="mt-6 text-2xl transition-transform duration-500 group-hover:translate-x-2">
                  →
                </div>
              </Link>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
